import { Extension } from '@tiptap/core';
import { Node } from '@tiptap/pm/model';
import { EditorState, Plugin, PluginKey, Transaction } from '@tiptap/pm/state';
import { Decoration, DecorationSet } from '@tiptap/pm/view';

declare module '@tiptap/core' {
  interface Commands<ReturnType> {
    dictionaryHighlight: {
      /** The dictionary's words to look for; loaded once the terms query returns. */
      setDictionaryWords: (words: DictionaryWord[]) => ReturnType;
      /** Switches the underline on or off per kind; matches are found either way. */
      setDictionaryHighlight: (highlight: { rejected?: boolean; accepted?: boolean }) => ReturnType;
    };
  }
}

/** `rejected` for nei-ord; `accepted` for ja-ord and synonyms. */
export type DictionaryWordKind = 'rejected' | 'accepted';

/** A word from the dictionary and the term it belongs to. */
export interface DictionaryWord {
  word: string;
  kind: DictionaryWordKind;
  preferred: string;
  termId: string;
}

export interface DictionaryMatch extends DictionaryWord {
  from: number;
  to: number;
  /** The text as written, e.g. «Klosettet» for the nei-ord «klosett». */
  text: string;
}

interface DictionaryHighlightState {
  words: DictionaryWord[];
  highlight: Record<DictionaryWordKind, boolean>;
  matches: DictionaryMatch[];
  decorations: DecorationSet;
}

type Meta = { words?: DictionaryWord[]; highlight?: { rejected?: boolean; accepted?: boolean } };

export const dictionaryHighlightKey = new PluginKey<DictionaryHighlightState>('dictionaryHighlight');

const EMPTY: DictionaryMatch[] = [];

/** The dictionary words of one kind found in the document, in document order. */
export const getDictionaryMatches = (state: EditorState, kind: DictionaryWordKind): DictionaryMatch[] => {
  const matches = dictionaryHighlightKey.getState(state)?.matches;
  return matches?.filter((m) => m.kind === kind) ?? EMPTY;
};

const CLASS_BY_KIND: Record<DictionaryWordKind, string> = { rejected: 'rejected-term', accepted: 'dictionary-term' };

/**
 * Finds the dictionary's words in the text: the whole word, optionally followed by a common
 * Norwegian ending (klosett, klosetten, klosettet, klosetter, klosettene), so compounds such as
 * «klosettstuss» are not matched. The matches are always kept for the sidebar and its badge; the
 * underline is only drawn for the kinds that are switched on.
 */
export const DictionaryHighlight = Extension.create({
  name: 'dictionaryHighlight',

  addCommands() {
    const setMeta =
      (meta: Meta) =>
      ({ tr, dispatch }: { tr: Transaction; dispatch?: (tr: Transaction) => void }) => {
        if (dispatch) {
          tr.setMeta(dictionaryHighlightKey, meta);
          dispatch(tr);
        }
        return true;
      };
    return {
      setDictionaryWords: (words) => setMeta({ words }),
      setDictionaryHighlight: (highlight) => setMeta({ highlight }),
    };
  },

  addProseMirrorPlugins() {
    return [
      new Plugin<DictionaryHighlightState>({
        key: dictionaryHighlightKey,
        state: {
          init: (_, { doc }) => compute(doc, [], { rejected: false, accepted: false }),
          apply: (tr, old) => {
            const meta = tr.getMeta(dictionaryHighlightKey) as Meta | undefined;
            if (!meta && !tr.docChanged) return old;
            return compute(tr.doc, meta?.words ?? old.words, { ...old.highlight, ...meta?.highlight });
          },
        },
        props: {
          decorations(state) {
            return this.getState(state)?.decorations;
          },
        },
      }),
    ];
  },
});

// Definite and plural endings; -n, -ne and -r cover words already ending in -e (pipe → pipen, pipene, piper).
const ENDINGS = '(?:ene|ne|en|et|er|e|n|r)?';

// ponytail: rescans the whole document on every change; fine at guide sizes, scan only the changed ranges if it gets slow.
// A word split across text nodes (e.g. half of it bold) is not found.
function compute(doc: Node, words: DictionaryWord[], highlight: Record<DictionaryWordKind, boolean>): DictionaryHighlightState {
  // A word belongs to at most one term in the dictionary, so the first entry wins only on bad data.
  const byWord = new Map<string, DictionaryWord>();
  for (const w of words) if (w.word && !byWord.has(w.word.toLowerCase())) byWord.set(w.word.toLowerCase(), w);
  const matches: DictionaryMatch[] = [];

  if (byWord.size > 0) {
    const alternatives = [...byWord.keys()].sort((a, b) => b.length - a.length).map(escapeRegExp);
    const regex = new RegExp(`(?<![\\p{L}\\p{N}])(${alternatives.join('|')})${ENDINGS}(?![\\p{L}\\p{N}])`, 'giu');

    doc.descendants((node, pos) => {
      if (!node.isText || !node.text) return;
      for (const m of node.text.matchAll(regex)) {
        const word = byWord.get(m[1].toLowerCase());
        if (word) matches.push({ ...word, from: pos + m.index!, to: pos + m.index! + m[0].length, text: m[0] });
      }
    });
  }

  const shown = matches.filter((m) => highlight[m.kind]);
  const decorations =
    shown.length > 0
      ? DecorationSet.create(
          doc,
          shown.map((m) =>
            Decoration.inline(m.from, m.to, {
              class: CLASS_BY_KIND[m.kind],
              'data-term-id': m.termId,
              title: m.kind === 'rejected' ? `Bruk «${m.preferred}»` : m.preferred,
            }),
          ),
        )
      : DecorationSet.empty;

  return { words, highlight, matches, decorations };
}

const escapeRegExp = (s: string) => s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
